import { useState, useEffect, useCallback } from 'react'
import { apiUrl, authFetch } from '../../lib/api'

interface AnalyticsData {
  totalViews: number; uniqueVisitors: number
  last7days: { day: string; count: number }[]
  topEvents: { event: string; count: number }[]
}

export function AnalyticsDashboard({ projectId }: { projectId?: string }) {
  const [data, setData] = useState<AnalyticsData | null>(null)
  const [projects, setProjects] = useState<{ id: string; name: string }[]>([])
  const [selectedProject, setSelectedProject] = useState(projectId ?? '')

  useEffect(() => {
    // Load projects from both database and workspace folders
    Promise.all([
      authFetch(apiUrl('/api/projects')).then(r => r.json()).catch(() => []),
      authFetch(apiUrl('/api/files/list')).then(r => r.json()).catch(() => []),
    ]).then(([dbProjects, fileTree]) => {
      const list: { id: string; name: string }[] = []
      // Add database projects
      const dbList = Array.isArray(dbProjects) ? dbProjects : []
      for (const p of dbList) list.push({ id: p.id, name: p.name })
      // Add workspace folders as projects (if not already in db)
      const folders = Array.isArray(fileTree) ? fileTree.filter((n: any) => n.type === 'folder') : []
      for (const f of folders) {
        if (!list.some(p => p.name === f.name)) {
          list.push({ id: f.name, name: f.name })
        }
      }
      setProjects(list)
      if (!selectedProject && list.length) setSelectedProject(list[0].id)
    })
  }, [])

  const load = useCallback(() => {
    if (!selectedProject) return
    authFetch(apiUrl(`/api/analytics/${selectedProject}`)).then(r => {
      if (!r.ok) throw new Error('not found')
      return r.json()
    }).then(d => {
      if (d && d.last7days) setData(d)
      else throw new Error('no data')
    }).catch(() => {
      // Show empty analytics for projects with no traffic yet
      setData({
        totalViews: 0, uniqueVisitors: 0,
        last7days: Array.from({ length: 7 }, (_, i) => {
          const d = new Date(); d.setDate(d.getDate() - (6 - i))
          return { day: d.toISOString().split('T')[0], count: 0 }
        }),
        topEvents: [],
      })
    })
  }, [selectedProject])

  useEffect(() => { load() }, [load])

  const maxCount = Math.max(...(data?.last7days?.map(d => d.count) ?? [1]), 1)

  return (
    <div style={{ height: '100%', overflow: 'auto', padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <span style={{ fontSize: 16 }}>📊</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 14, color: 'var(--text-0)' }}>Analytics</span>
        <div style={{ flex: 1 }} />
        <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)} style={{
          padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)',
          background: 'var(--bg-1)', color: 'var(--text-1)', fontSize: 10,
          fontFamily: 'var(--font-mono)', cursor: 'pointer',
        }}>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {!data ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
          Select a project to view analytics
        </div>
      ) : (
        <>
          {/* Stats cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
            <StatCard label="Total Views" value={data.totalViews.toLocaleString()} icon="👁" />
            <StatCard label="Unique Visitors" value={data.uniqueVisitors.toLocaleString()} icon="👤" />
            <StatCard label="Events Today" value={(data.last7days.at(-1)?.count ?? 0).toString()} icon="⚡" />
          </div>

          {/* Chart */}
          <div style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 20 }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 11, color: 'var(--text-0)', marginBottom: 12 }}>
              Views — Last 7 Days
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 80 }}>
              {data.last7days.map((d, i) => (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 8, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{d.count}</span>
                  <div style={{
                    width: '100%', borderRadius: '4px 4px 0 0', minHeight: 4,
                    height: `${(d.count / maxCount) * 60}px`,
                    background: 'var(--accent)', opacity: 0.7,
                  }} />
                  <span style={{ fontSize: 7, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>
                    {new Date(d.day).toLocaleDateString([], { weekday: 'short' })}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Top events */}
          {data.topEvents.length > 0 && (
            <div style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 11, color: 'var(--text-0)', marginBottom: 10 }}>
                Top Events
              </div>
              {data.topEvents.map((e, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-1)', flex: 1 }}>{e.event}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700, color: 'var(--accent)' }}>{e.count}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

function StatCard({ label, value, icon }: { label: string; value: string; icon: string }) {
  return (
    <div style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 10, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <span style={{ fontSize: 14 }}>{icon}</span>
        <span style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{label}</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-0)' }}>{value}</div>
    </div>
  )
}
